import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowData,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useState } from "react";

const InternalButtonsMap = {
  globalFilterToggle: MRT_ToggleGlobalFilterButton,
  filterToggle: MRT_ToggleFiltersButton,
  showHideColumns: MRT_ShowHideColumnsButton,
  // densePaddingToggle: MRT_ToggleDensePaddingButton,
  fullScreenToggle: MRT_ToggleFullScreenButton,
};
type StateDispatch<T = any> = React.Dispatch<React.SetStateAction<T>>;

interface CommonTableProps<T extends MRT_RowData> extends MRT_TableOptions<T> {
  data: T[];
  columns: MRT_ColumnDef<T>[];
  isLoading: boolean;
  internalActionsOrder?: (keyof typeof InternalButtonsMap | "customisedDownloadReportButton")[];
  customDownloadReportButton?: React.ReactNode;
  additionalState?: MRT_TableOptions<T>["state"];
  additionalInitialState?: MRT_TableOptions<T>["initialState"];
  setSelectedRow?: StateDispatch<T[]> | StateDispatch<Record<string, unknown>[]>;
  clearRowsSelection?: boolean;
  setClearRowsSelection?: StateDispatch<boolean>;
}

const ReactTable = <T extends MRT_RowData>({
  data,
  columns,
  isLoading = false,
  ...props
}: CommonTableProps<T>): JSX.Element => {
  const [rowSelection, setRowSelection] = useState({});

  const tableOptions: MRT_TableOptions<T> = {
    columns,
    data,
    // Disable row selection
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enablePagination: true,
    enableFullScreenToggle: true,
    enableFacetedValues: true,
    enableColumnPinning: true,
    // Enable cell editing
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableDensityToggle: false,
    selectAllMode: "all",
    onRowSelectionChange: setRowSelection,

    // Simplified table container
    muiTablePaperProps: ({ table }) => ({
      style: {
        left: table.getState().isFullScreen ? "70px" : undefined,
        width: table.getState().isFullScreen ? "95vw" : undefined,
      },
    }),

    muiFilterDatePickerProps: {
      format: "DD/MM/YYYY",
      slotProps: {
        openPickerButton: { size: "small" },
        textField: { size: "small" },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: "calc(100vh - 220px)",
      },
    },
    muiFilterTextFieldProps: {
      SelectProps: {
        SelectDisplayProps: {
          style: {
            height: "20px",
          },
        },
      },
    },

    state: {
      isLoading,
      rowSelection: rowSelection,
      ...props.additionalState,
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
      density: "compact",
      showColumnFilters: true,
      ...props.additionalInitialState,
    },

    ...props,
  };

  const table = useMaterialReactTable(tableOptions);
  const { getSelectedRowModel } = table;

  useEffect(() => {
    const filterSelectedFlatRows = getSelectedRowModel().flatRows.map((item) => item.original);

    if (props.setSelectedRow) props.setSelectedRow(filterSelectedFlatRows);
  }, [rowSelection, props.setSelectedRow]);

  useEffect(() => {
    if (props.clearRowsSelection && props.setClearRowsSelection) {
      setRowSelection({});
      props.setClearRowsSelection(false);
    }
  }, [data, rowSelection, props.setSelectedRow, props.clearRowsSelection]);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MaterialReactTable table={table} />
    </LocalizationProvider>
  );
};

export default ReactTable;
